import React, { useMemo } from 'react';
import PropTypes from 'prop-types';
import ReactEChartsCore from 'echarts-for-react/lib/core';
import { LineChart } from 'echarts/charts';
import {
    DatasetComponent,
    GridComponent,
    LegendComponent,
    ToolboxComponent,
    TooltipComponent,
} from 'echarts/components';
import * as echarts from 'echarts/core';
import { UniversalTransition } from 'echarts/features';
import { CanvasRenderer } from 'echarts/renderers';
import { Card } from 'react-bootstrap';

echarts.use([
    DatasetComponent,
    TooltipComponent,
    ToolboxComponent,
    GridComponent,
    LegendComponent,
    LineChart,
    CanvasRenderer,
    UniversalTransition
]);

// Componente para gráfica mensual
const MonthlyChart = ({
    selectedYear,
    selectedVendor,
    monthlyData,
    onYearChange,
    onVendorChange,
    vendedores
}) => {
    const months = ['Ene', 'Feb', 'Mar', 'Abr', 'May', 'Jun', 'Jul', 'Ago', 'Sep', 'Oct', 'Nov', 'Dic'];
    const currentYear = new Date().getFullYear();

    const years = [];
    for (let year = currentYear; year >= 2020; year--) {
        years.push(year);
    }

    const monthlyOption = useMemo(() => {
        // Procesar los datos que llegan como array de objetos
        const currentYearData = monthlyData[selectedYear] || [];
        const previousYearData = monthlyData[selectedYear - 1] || [];

        // Función para procesar datos por vendedor y mes
        const processDataByVendor = (data, vendorFilter) => {
            // Filtrar por vendedor si no es "TODOS"
            const filteredData = vendorFilter === 'TODOS'
                ? data
                : data.filter(item => item.vNombre === vendorFilter || item.vUsuario === vendorFilter);

            const monthlyTotals = new Array(12).fill(0);

            filteredData.forEach(item => {
                const monthIndex = item.mes - 1;
                if (monthIndex >= 0 && monthIndex < 12) {
                    monthlyTotals[monthIndex] += item.totalMXN || 0;
                }
            });

            return monthlyTotals;
        };

        const currentProcessed = processDataByVendor(currentYearData, selectedVendor);
        const previousProcessed = processDataByVendor(previousYearData, selectedVendor);

        return {
            title: {
                text: 'Ventas por mes',
                left: 'left',
                textStyle: { fontSize: 18, fontWeight: 'bold', color: '#374151' }
            },
            tooltip: {
                trigger: 'axis',
                formatter: function (params) {
                    const formatCurrency = (value) => {
                        return new Intl.NumberFormat('es-MX', {
                            style: 'currency',
                            currency: 'MXN',
                            minimumFractionDigits: 0,
                            maximumFractionDigits: 0
                        }).format(value);
                    };

                    let result = `📅 ${params[0].axisValue}<br/>`;
                    params.forEach(param => {
                        const marker = `<span style="display:inline-block;margin-right:4px;border-radius:10px;width:10px;height:10px;background-color:${param.color};"></span>`;
                        result += `${marker}${param.seriesName}: <b>${formatCurrency(param.value)}</b><br/>`;
                    });
                    return result;
                }
            },
            legend: {
                data: [selectedYear.toString(), (selectedYear - 1).toString()],
                top: 'top',
                right: 'right'
            },
            grid: { left: '3%', right: '4%', bottom: '3%', containLabel: true },
            xAxis: { type: 'category', boundaryGap: false, data: months },
            yAxis: { type: 'value' },
            series: [
                {
                    name: selectedYear.toString(),
                    type: 'line',
                    data: currentProcessed,
                    lineStyle: { color: '#3b82f6', width: 3 },
                    itemStyle: { color: '#3b82f6' },
                    symbol: 'circle',
                    symbolSize: 6
                },
                {
                    name: (selectedYear - 1).toString(),
                    type: 'line',
                    data: previousProcessed,
                    lineStyle: { color: '#fbbf24', width: 3 },
                    itemStyle: { color: '#fbbf24' },
                    symbol: 'circle',
                    symbolSize: 6
                }
            ]
        };
    }, [selectedYear, selectedVendor, monthlyData]);

    return (
        <Card className=" rounded-lg shadow-md p-4 mb-4 w-full">
            <div className="row mb-3">
                {/* Select de Año */}
                <div className="col-md-3 col-6">
                    <div className="form-group">
                        <label className="form-label  text-uppercase small fw-medium mb-1">
                            Año
                        </label>
                        <select
                            value={selectedYear}
                            onChange={(e) => onYearChange(Number(e.target.value))}
                            className="form-select form-select-sm"
                        >
                            {years.map(year => (
                                <option key={year} value={year}>{year}</option>
                            ))}
                        </select>
                    </div>
                </div>

                {/* Select de Vendedores */}
                <div className="col-md-5 col-6">
                    <div className="form-group">
                        <label className="form-label  text-uppercase small fw-medium mb-1">
                            Vendedores
                        </label>
                        <select
                            value={selectedVendor}
                            onChange={(e) => onVendorChange(e.target.value)}
                            className="form-select form-select-sm"
                        >
                            {vendedores.map(vendor => (
                                <option key={vendor} value={vendor}>
                                    {vendor === 'TODOS' ? 'TODOS LOS VENDEDORES' : vendor}
                                </option>
                            ))}
                        </select>
                    </div>
                </div>
            </div>

            <ReactEChartsCore
                echarts={echarts}
                option={monthlyOption}
                style={{ height: '300px', width: '100%' }}
            />
        </Card>
    );
};

MonthlyChart.propTypes = {
    selectedYear: PropTypes.number.isRequired,
    selectedVendor: PropTypes.string.isRequired,
    monthlyData: PropTypes.objectOf(
        PropTypes.arrayOf(
            PropTypes.shape({
                idUsuario: PropTypes.number.isRequired,
                mes: PropTypes.number.isRequired,
                totalMXN: PropTypes.number.isRequired,
                vNombre: PropTypes.string.isRequired,
                vUsuario: PropTypes.string.isRequired
            })
        )
    ).isRequired,
    onYearChange: PropTypes.func.isRequired,
    onVendorChange: PropTypes.func.isRequired,
    vendedores: PropTypes.arrayOf(PropTypes.string).isRequired
};

export default MonthlyChart;